import * as R from 'ramda';
import { GetOrderSummaryOutput } from '../../../services/modules/order/order.types';
import { SendOfflineOrderToSyncInput } from '../../../services/modules/orders/orders.types';
import { Classification } from '../../classifications/classification.types';
import { GetOrderConditionDTO } from '../../orders/conditions/dtos/orderCondition.dto';
import {
  GetBrandDTO,
  Order,
  OrderBlockReason,
  OrderHistory,
  OrderListType,
  SynchronizedType,
} from '../../orders/order.types';
import { OrderPaymentStatus } from '../../orders/payment/orderPayment.types';
import OrderProductsGroupByFactory from '../../orders/products/helpers/groupBy/OrderProductsGroupByFactory';
import {
  GetOrderItemsStatus,
  HeaderSection,
  OrderHeadersGroupByEnum,
  OrderProduct,
  PriceCompositionTotals,
} from '../../orders/products/orderProducts.types';
import { normalizeSectionIdentifier } from '../../orders/products/section/orderProductsSection.utils';
import PriceCompositionUtils from '../../products/prices/utils/priceComposition.utils';
import { User } from '../../user-admin/user.types';
import { OfflineDatabaseOrder } from '../database/offlineDatabase/types/offlineDatabaseOrder.types';
import { OfflinePriceToSize } from '../database/offlineDatabase/types/offlineDatabaseProductColorPriceInfo.types';
import { OfflineOrder } from '../domain/offlineOrder/OfflineOrder';
import { OfflineStatus } from '../domain/offlineOrder/offlineOrder.types';
import { OfflineOrderProduct } from '../domain/offlineOrder/orderProduct/OfflineOrderProduct';
import { OfflineCategoryTransformers } from './OfflineDatabaseCategory.transformers';
import { OfflineClassificationTransformers } from './OfflineDatabaseClassification.transformers';
import { OfflineCollectionTransformers } from './OfflineDatabaseCollection.transformers';
import { OfflineCustomerTransformers } from './OfflineDatabaseCustomer.transformers';
import { OfflinePriceTableTransformers } from './OfflineDatabasePriceTable.transformers';
import { OfflineDatabaseSalesRepresentativeTransformer } from './OfflineDatabaseSalesRepresentative.transformers';
import { OfflineDatabaseSellerTransformer } from './OfflineDatabaseSeller.transformers';
import { OfflineOrderProductTransformers } from './OfflineOrderProduct.transformers';
import { OfflineProductColorTransformers } from './productColor/OfflineDatabaseProductColor.transformers';

const getOrderCodeToOnline = (order: OfflineOrder): string => {
  return `${order.code.toString()}*`;
};

export class OfflineOrderTransformers {
  static toCreateOfflineDatabaseOrder(order: OfflineOrder): OfflineDatabaseOrder {
    return {
      id: order.id,
      status: order.status,
      seller: order.seller,
      brandId: order.brandId,
      customer: order.customer,
      createdAt: order.createdAt,
      updatedAt: order.updatedAt,
      priceTable: order.priceTable,
      salesRepresentative: order.salesRepresentative,
    };
  }

  static toOfflineDatabaseOrder(order: OfflineOrder): OfflineDatabaseOrder {
    return {
      id: order.id,
      code: order.code,
      status: order.status,
      seller: order.seller,
      brandId: order.brandId,
      customer: order.customer,
      metadata: order.metadata,
      updatedAt: order.updatedAt,
      createdAt: order.createdAt,
      priceTable: order.priceTable,
      observation: order.observation,
      salesRepresentative: order.salesRepresentative,
      products: order.products?.map(OfflineOrderProductTransformers.toOfflineDatabaseOrderProduct),
    };
  }

  static toSendOfflineOrderToSyncInput(order: OfflineOrder): SendOfflineOrderToSyncInput {
    return {
      id: order.id,
      code: order.code,
      seller: order.seller,
      brandId: order.brandId,
      customer: order.customer,
      metadata: order.metadata,
      subTotal: order.subTotal,
      createdAt: order.createdAt,
      priceTable: order.priceTable,
      totalValue: order.totalValue,
      totalItems: order.totalItems,
      status: OfflineStatus.OFFLINE,
      totalPieces: order.totalPieces,
      observation: order.observation,
      totalGrossValue: order.grossValue,
      salesRepresentative: order.salesRepresentative,
      products: order.products?.map(OfflineOrderProductTransformers.toOfflineDatabaseOrderProduct),
    };
  }

  static toOnlineOrder(order: OfflineOrder): Order<true> {
    return {
      id: order.id,
      splitted: false,
      archived: false,
      splitSettings: [],
      type: order.orderType,
      externalCode: order.metadata?.onlineMetadata?.onlineOrderCode,
      status: order.status,
      totalDiscountValue: 0,
      code: getOrderCodeToOnline(order),
      subTotal: order.subTotal,
      createdAt: order.createdAt,
      totalValue: order.totalValue,
      totalItems: order.totalItems,
      totalPieces: order.totalPieces,
      totalRetail: order.totalRetail,
      availability: [order.orderType],
      classification: {} as Classification,
      paymentStatus: OrderPaymentStatus.WAITING,
      brand: { id: order.brandId } as GetBrandDTO,
      synchronized: SynchronizedType.NOT_SYNCHRONIZED,
      priceTable: OfflinePriceTableTransformers.toOnlinePriceTable(order.priceTable),
      seller: order.seller
        ? OfflineDatabaseSellerTransformer.toOnlineSeller(order.seller)
        : undefined,
      salesRepresentative: order.salesRepresentative
        ? OfflineDatabaseSalesRepresentativeTransformer.toOnlineSalesRepresentative(
            order.salesRepresentative,
          )
        : undefined,
      customer: order.customer
        ? OfflineCustomerTransformers.toOnlineCustomer(order.customer)
        : undefined,
      orderGatewayPayments: [],
      signedContracts: [],
    };
  }

  static toOrderListType(order: OfflineOrder): OrderListType {
    const codeToList = getOrderCodeToOnline(order);

    return {
      baseCode: codeToList,
      isChild: false,
      isParent: false,
      orderType: order.orderType,
      orderId: order.id,
      orderSplitted: false,
      orderArchived: false,
      brandCompanyName: '',
      orderDiscountValue: 0,
      parentTotalDiscount: 0,
      parentTotalCharges: 0,
      parentTotalShippingCost: 0,
      orderBlockReason: OrderBlockReason.NONE,
      brandId: order.brandId,
      orderSplitSettings: [],
      brandCommercialName: '',
      orderStatus: order.status,
      orderSubTotal: order.subTotal,
      parentSubTotal: order.subTotal,
      totalPieces: order.totalPieces,
      orderUpdatedAt: order.updatedAt.toISOString(),
      parentUpdatedAt: order.updatedAt.toISOString(),
      orderCreatedAt: order.createdAt.toISOString(),
      parentCreatedAt: order.createdAt.toISOString(),
      orderCode: codeToList,
      orderTotalValue: order.totalValue,
      parentTotalItems: order.totalItems,
      parentTotalValue: order.totalValue,
      parentTotalPieces: order.totalPieces,
      classification: {} as Classification,
      sellerName: order.seller?.name || '',
      sellerEmail: order.seller?.email || '',
      sellerId: order.seller?.id as unknown as number,
      orderPaymentStatus: OrderPaymentStatus.WAITING,
      orderSynchronized: SynchronizedType.NOT_SYNCHRONIZED,
      customerCode: order.customer?.code || '',
      customerId: order.customer?.id as unknown as number,
      customerCompanyName: order.customer?.companyName || '',
      customerCommercialName: order.customer?.commercialName || '',
      orderSalesRepresentativeName: order.salesRepresentative?.name || '',
      orderExternalCode: order.metadata?.onlineMetadata?.onlineOrderCode || '',
      orderSalesRepresentativeId: order.salesRepresentative?.id as unknown as number,
      availabilities: R.uniq(order.products?.map((product) => product.availability) || []),
      lastGatewayPaymentStatus: null,
      lastGatewayPaymentUpdatedAt: null,
    };
  }

  static toOrderProducts(order: OfflineOrder): OrderProduct[] {
    const orderProducts = order.products?.map<OrderProduct>(this.offlineOrderProductToOrderProduct);

    return orderProducts || [];
  }

  static offlineOrderProductToOrderProduct(orderProduct: OfflineOrderProduct): OrderProduct {
    return {
      code: orderProduct.product.code,
      name: orderProduct.product.name,
      updatingAvailability: false,
      productId: orderProduct.product.id,
      availability: orderProduct.availability,
      deliveryWindow: orderProduct.deliveryWindow,
      productColorId: orderProduct.productColor.id,
      allowChangePreOrderGrid: orderProduct.productColor.allowChangePreOrderGrid,
      allowChangeImmediateGrid: orderProduct.productColor.allowChangeImmediateGrid,
      category: orderProduct.metadata?.productCategory
        ? OfflineCategoryTransformers.toListCategoryDTO(orderProduct.metadata?.productCategory)
        : undefined,
      collection: orderProduct.metadata?.productCollection
        ? OfflineCollectionTransformers.toCollection(orderProduct.metadata?.productCollection)
        : undefined,
      colorClassifications: orderProduct.metadata?.productColorClassifications
        ? orderProduct.metadata.productColorClassifications.map(
            OfflineClassificationTransformers.toColorClassification,
          )
        : undefined,
      kit: OfflineOrderProductTransformers.offlineOrderProductKitToOrderProductKit(
        orderProduct.kit,
      ),
      color: OfflineProductColorTransformers.toESProductColor(orderProduct.productColor),
      media: OfflineProductColorTransformers.toESProductMedia(orderProduct.productColor),
    };
  }

  private static orderProductsToPriceCompositionTotals(
    orderProducts: OfflineOrderProduct[],
  ): PriceCompositionTotals[] {
    const totals = new Map<string, PriceCompositionTotals>();

    for (const product of orderProducts) {
      const pricesToSizes = product?.metadata?.productColorPriceInfo?.pricesToSizes || [];

      const priceInfoBySize = new Map<string, OfflinePriceToSize>(
        pricesToSizes.map((p) => [p.sizeId, p]),
      );

      for (const currentGrid of product.grid) {
        const priceInfo = priceInfoBySize.get(currentGrid.size.id);

        const quantity = currentGrid.quantity;
        const compositions = priceInfo?.priceComposition;

        if (!compositions || !quantity) {
          continue;
        }

        for (const composition of compositions) {
          const key = composition.id;
          const valueToIncrement = composition.price * quantity;

          const existing = totals.get(key);
          if (existing) {
            existing.totalValue += valueToIncrement;
          } else {
            totals.set(key, {
              id: composition.id,
              totalValue: valueToIncrement,
              label: PriceCompositionUtils.formatLabel(composition.label),
            });
          }
        }
      }
    }

    return Array.from(totals.values());
  }

  static toOrderSummary(
    order: OfflineOrder,
    {
      classificationTypeIds,
      view = [OrderHeadersGroupByEnum.DEFAULT],
    }: ToOrderSummaryGroupByInfo = {},
  ): GetOrderSummaryOutput {
    const orderProducts = this.toOrderProducts(order);

    const groupByFactory = new OrderProductsGroupByFactory();
    const groupByBuilder = groupByFactory.create(view);

    const sections = groupByBuilder.group({
      orderProducts,
      info: !classificationTypeIds?.length
        ? undefined
        : { classificationTypeId: classificationTypeIds },
    });

    const baseDatabaseOrder = this.toOfflineDatabaseOrder(order);

    const headerSections: HeaderSection[] = sections.map((section) => {
      const currentSectionProducts = section.products;

      const currentSectionProductsFromOfflineOrder = currentSectionProducts.reduce<
        OfflineOrderProduct[]
      >((acc, product) => {
        const found = order.getOrderProduct({
          availability: product.availability,
          productColorId: product.productColorId,
          deliveryWindowId: product.deliveryWindow?.id,
        });

        if (found) {
          acc.push(found);
        }

        return acc;
      }, []);

      const orderDbForSection: OfflineDatabaseOrder = {
        ...baseDatabaseOrder,
        products: currentSectionProductsFromOfflineOrder.map(
          OfflineOrderProductTransformers.toOfflineDatabaseOrderProduct,
        ),
      };

      const fakeOrderToGetTotals = OfflineOrder.fromOfflineDatabase(orderDbForSection);

      return {
        total: fakeOrderToGetTotals.totalValue,
        pieces: fakeOrderToGetTotals.totalPieces,
        quantity: fakeOrderToGetTotals.totalPieces,
        categoryId: section.category?.id,
        availability: section.availability,
        collectionId: section.collection?.id,
        productCode: section.products[0]?.code,
        subCategoryId: section.subCategory?.id,
        incomeStatus: {} as GetOrderItemsStatus,
        deliveryWindowId: section.deliveryWindow?.id,
        classificationId: section.colorClassification?.id,
        multipleIdentifier: normalizeSectionIdentifier(section.identifier),
        priceCompositionTotals: !fakeOrderToGetTotals.products
          ? []
          : this.orderProductsToPriceCompositionTotals(fakeOrderToGetTotals.products),
      };
    });

    const total = {
      discount: 0,
      total: order.totalValue,
      subtotal: order.subTotal,
      pieces: order.totalPieces,
      quantity: order.totalItems,
      grossValue: order.grossValue,
      totalRetail: order.totalRetail,
      deliveries: order.deliveryQuantity,
      totalChargesValue: 0,
      totalShippingCostValue: 0,
    };

    return {
      affectedOrderProducts: [],
      headers: {
        sections: headerSections,
        type: view[0],
      },
      calculate: {
        paymentStatus: '',
        total,
        conditions: [{ id: this.toOrderConditionId(order), ...total }],
      },
    };
  }

  static toOrderConditionId(order: OfflineOrder) {
    return `${order.id}-order-condition`;
  }

  static toGetOrderConditionDTO(order: OfflineOrder): GetOrderConditionDTO {
    return {
      customFields: [],
      orderType: order.orderType,
      id: this.toOrderConditionId(order),
      observation: order.observation,
    };
  }

  static toOrderHistories(order: OfflineOrder, user?: User): OrderHistory[] {
    const offlineHistory: OrderHistory = {
      user: user,
      newState: OfflineStatus.OFFLINE,
      id: `${order.id}-offline-history`,
      createdAt: order.createdAt.toISOString(),
      totalItems: order.totalItems,
      totalPieces: order.totalPieces,
      subTotal: order.subTotal,
      orderCode: `${order.code}`,
      parentOrderId: null,
      parentOrderCode: null,
    };

    const errorHistory: OrderHistory | undefined = order.metadata?.errorMetadata
      ? {
          newState: OfflineStatus.ERROR_SYNCING,
          id: `${order.id}-offline-error-history`,
          createdAt: order.metadata.errorMetadata.lastSyncingError.toISOString(),
          orderCode: `${order.code}`,
          parentOrderId: null,
          parentOrderCode: null,
        }
      : undefined;

    const orderHistories = errorHistory ? [offlineHistory, errorHistory] : [offlineHistory];

    return orderHistories;
  }
}

interface ToOrderSummaryGroupByInfo {
  view?: OrderHeadersGroupByEnum[];
  classificationTypeIds?: string[];
}
